import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { GovIcon, GovMessage } from '@gov-design-system-ce/react';
import {
	useAuthorizedLabel,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useEPeticeDetailContext } from '@gov-nx/module/page';
import { Button, ContentLayout, Dialog, LayoutGap } from '@gov-nx/ui/web';
import { SouhlasCheckbox } from '../../../Autorizace/SouhlasCheckbox';
import { MarkdownRender } from '../../../Markdown/MarkdownRender';
import { EPetitionPetitionersAttachments } from './EPetitionPetitioners';

export interface EPetitionDetailViewProps {
	code: PageCode;
}

const SIGN_FORM_ID = 'EPETICE_SIGN_FORM';
const REVOKE_FORM_ID = 'EPETICE_REVOKE_FORM';

export const EPetitionSign = ({ code }: EPetitionDetailViewProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const authLabel = useAuthorizedLabel();
	const {
		petition,
		controls,
		setControls,
		ePetitionSign,
		loginInfo,
		signFormDefinition,
		handleSignSubmit,
		revokeFormDefinition,
		handleRevokeSubmit,
	} = useEPeticeDetailContext();

	return (
		<>
			{loginInfo.isDataBoxLoginType ? (
				<GovMessage
					color="warning"
					type="subtle"
					icon={
						<GovIcon
							type="components"
							name="info-circle-fill"
						/>
					}>
					<MarkdownRender>{ts('prihlaseni.datovka-varovani')}</MarkdownRender>
				</GovMessage>
			) : null}
			<ContentLayout>
				<LayoutGap gap="xl">
					<div className="flex flex-col gap-4 [ md:flex-row md:justify-between md:items-center ]">
						<h4 className="text-headline-m! text-(--text-secondary)">
							{ts('podpis.aktualni-pocet-podpisu')} {petition?.pocetPodpisu}
						</h4>
						<div>
							{ePetitionSign.isFounder ? (
								<Button
									color="primary"
									type="solid"
									size="m"
									isDisabled={ePetitionSign.isFounder}
									aria-label={ts('akce.wcag.podepsat')}>
									{ts('akce.podepsat')}
								</Button>
							) : null}
							{ePetitionSign.canSign ? (
								<Button
									color="primary"
									type="solid"
									size="m"
									onClick={() => setControls({ isSignModal: true })}
									aria-label={authLabel(
										ts('akce.wcag.podepsat'),
										ePetitionSign.isAuthorizationEnforced
									)}>
									{authLabel(
										ts('akce.podepsat'),
										ePetitionSign.isAuthorizationEnforced
									)}
								</Button>
							) : null}
							{ePetitionSign.canRevoke ? (
								<Button
									color="primary"
									type="outlined"
									size="m"
									onClick={() => setControls({ isRevokeModal: true })}
									aria-label={ts('akce.wcag.odvolat')}>
									{ts('akce.odvolat')}
								</Button>
							) : null}
							<Dialog
								isOpen={controls.isSignModal}
								onClose={() => {
									setControls({ isSignModal: false });
									signFormDefinition.formMethods.reset();
								}}
								title={<h3>{ts('podpis.podepsat-petici')}</h3>}
								footer={
									<>
										<Button
											form={SIGN_FORM_ID}
											nativeType="submit"
											isDisabled={controls.isSignProcessing}
											isLoading={controls.isSignProcessing}
											color="primary"
											type="solid"
											size="m"
											aria-label={`${authLabel(
												ts('akce.wcag.souhlasim'),
												ePetitionSign.isAuthorizationEnforced
											)} ${petition?.nazev}`}>
											{authLabel(
												ts('akce.souhlasim'),
												ePetitionSign.isAuthorizationEnforced
											)}
										</Button>
										<Button
											isDisabled={controls.isSignProcessing}
											onClick={() => {
												setControls({ isSignModal: false });
												signFormDefinition.formMethods.reset();
											}}
											color="error"
											type="outlined"
											size="m"
											aria-label={ts('akce.wcag.nesouhlasim')}>
											{ts('akce.nesouhlasim')}
										</Button>
									</>
								}
								accessibleCloseLabel={ts('akce.wcag.zavrit')}>
								<GovFormWeb
									id={SIGN_FORM_ID}
									formDefinition={signFormDefinition}
									onSubmit={handleSignSubmit}>
									<p
										dangerouslySetInnerHTML={{
											__html: ts('podpis.proc-podepsat'),
										}}
									/>
									<SouhlasCheckbox
										show={ePetitionSign.isLowAuthorization}
										disabled={controls.isSignProcessing}
									/>
								</GovFormWeb>
							</Dialog>

							<Dialog
								isOpen={controls.isRevokeModal}
								onClose={() => {
									setControls({ isRevokeModal: false });
									revokeFormDefinition.formMethods.reset();
								}}
								title={<h3>{ts('podpis.odvolat-souhlas')}</h3>}
								footer={
									<>
										<Button
											form={REVOKE_FORM_ID}
											nativeType="submit"
											isDisabled={controls.isRevokeProcessing}
											isLoading={controls.isRevokeProcessing}
											color="primary"
											type="solid"
											size="m"
											aria-label={`${authLabel(
												ts('akce.wcag.odvolat'),
												ePetitionSign.isAuthorizationEnforcedRevoke
											)} ${petition?.nazev}`}>
											{authLabel(
												ts('akce.odvolat'),
												ePetitionSign.isAuthorizationEnforcedRevoke
											)}
										</Button>
										<Button
											isDisabled={controls.isRevokeProcessing}
											onClick={() => {
												setControls({ isRevokeModal: false });
												revokeFormDefinition.formMethods.reset();
											}}
											color="error"
											type="outlined"
											size="m"
											aria-label={ts('akce.wcag.zrusit')}>
											{ts('akce.zrusit')}
										</Button>
									</>
								}
								accessibleCloseLabel={ts('akce.wcag.zavrit')}>
								<GovFormWeb
									id={REVOKE_FORM_ID}
									formDefinition={revokeFormDefinition}
									onSubmit={handleRevokeSubmit}>
									<p
										dangerouslySetInnerHTML={{
											__html: ts('podpis.proc-odvolat'),
										}}
									/>
									<SouhlasCheckbox
										show={ePetitionSign.isLowAuthorizationRevoke}
										disabled={controls.isRevokeProcessing}
									/>
								</GovFormWeb>
							</Dialog>
						</div>
					</div>
					{ePetitionSign.isFounder ? (
						<GovMessage
							color="primary"
							type="subtle"
							icon={
								<GovIcon
									type="components"
									name="lightbulb-fill"
								/>
							}>
							{ts('info.automaticky-podpis')}
						</GovMessage>
					) : null}
					<EPetitionPetitionersAttachments />
				</LayoutGap>
			</ContentLayout>
		</>
	);
};
